import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Send,
  LayoutDashboard,
  Activity,
  Table2,
  ShieldCheck,
  Inbox,
  Database,
  FileText,
  ChevronRight,
  Bot,
  User,
  Loader2,
  Square,
  ArrowLeft,
} from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { useSharedAgentChat } from '../../contexts/AgentChatContext';
import { useGetSiteArtefactsQuery, type Artefact } from '../../redux/api/portfolioApi';
import { useListSiteEventsQuery } from '../../redux/api/eventsApi';
import type { SiteEvent } from '../../redux/api/eventsApi';
import type { AgentMessage, AgentMessagePart } from '../../types/agent';
import { SEVERITY_HEX_COLORS } from '../../constants/eventStyles';
import ThinkingBlock from '../chat/ThinkingBlock';
import ToolCallCard from '../chat/ToolCallCard';
import StreamingMarkdown from '../chat/StreamingMarkdown';
import Logo from '../Logo';

// ── Shortcut config ──────────────────────────────────────────────────

interface Shortcut {
  id: string;
  label: string;
  icon: React.ElementType;
  tabType: string;
}

const SHORTCUTS: Shortcut[] = [
  { id: 'portfolio', label: 'Portfolio Dashboard', icon: LayoutDashboard, tabType: 'portfoliodashboard' },
  { id: 'site', label: 'Site Dashboard', icon: Activity, tabType: 'siteanalysis' },
  { id: 'sites', label: 'Sites Table', icon: Table2, tabType: 'sitestable' },
  { id: 'qa', label: 'Drawing QA', icon: ShieldCheck, tabType: 'qaexecutivesummary' },
  { id: 'inbox', label: 'Project Inbox', icon: Inbox, tabType: 'projectinbox' },
  { id: 'dataroom', label: 'Dataroom', icon: Database, tabType: 'dataroom' },
];

// ── Helpers ──────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Component ────────────────────────────────────────────────────────

const HomeLandingTab: React.FC = () => {
  const [chatInput, setChatInput] = useState('');
  const [chatActive, setChatActive] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const currentSite = useAppSelector(state => state.site.currentSite);
  const siteId = currentSite?.site_id || currentSite?.id || '';
  const siteName = currentSite?.site_name || currentSite?.name || '';

  const { messages, sendMessage, isStreaming, stopStreaming } = useSharedAgentChat();

  // Fetch recent events for inbox preview
  const { data: eventsData } = useListSiteEventsQuery(
    { siteId, limit: 8, sort_by: 'occurred_at', sort_dir: 'desc' },
    { skip: !siteId }
  );
  const recentEvents: SiteEvent[] = useMemo(() => eventsData?.items || [], [eventsData]);

  // Fetch recent documents (artefacts)
  const tenantId = currentSite?.tenant_id;
  const { data: artefactsData } = useGetSiteArtefactsQuery(
    { tenant_id: tenantId!, site_id: siteId, limit: 4 },
    { skip: !siteId || !tenantId }
  );
  const recentDocs: Artefact[] = useMemo(() => artefactsData?.items || [], [artefactsData]);

  useEffect(() => {
    if (!chatActive) inputRef.current?.focus();
  }, [chatActive]);

  useEffect(() => {
    if (chatActive) {
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [chatActive]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(() => {
    const trimmed = chatInput.trim();
    if (!trimmed || isStreaming) return;
    sendMessage(trimmed);
    setChatInput('');
    if (!chatActive) setChatActive(true);
  }, [chatInput, isStreaming, sendMessage, chatActive]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openTab = useCallback((tabType: string) => {
    // If chat is active, transfer conversation to sidebar before navigating
    if (chatActive && messages.length > 0) {
      window.dispatchEvent(new CustomEvent('openAIChat', { detail: {} }));
    }
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType } }));
  }, [chatActive, messages]);

  const handleBackToLanding = useCallback(() => {
    setChatActive(false);
  }, []);

  const greeting = getGreeting();

  // ── Fullscreen chat mode ─────────────────────────────────────────

  if (chatActive) {
    return (
      <div
        className="h-full flex flex-col"
        style={{ background: 'var(--color-zone-recessed)' }}
      >
        {/* Chat header */}
        <div className="eg-toolbar-header flex-shrink-0 flex items-center gap-3 px-6 py-3">
          <button
            onClick={handleBackToLanding}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="" className="w-5 h-5" />
            <span className="text-sm font-semibold eg-text-primary">Assistant</span>
            <span
              className="px-1.5 py-0.5 text-[9px] font-medium rounded"
              style={{ backgroundColor: 'rgba(23, 196, 128, 0.1)', color: '#17c480' }}
            >
              Preview
            </span>
          </div>
          {siteName && (
            <span className="text-xs eg-text-muted ml-auto">{siteName}</span>
          )}
        </div>

        {/* Shortcuts row (compact) */}
        <div className="eg-toolbar-header flex-shrink-0 flex items-center gap-2 px-6 py-2 overflow-x-auto">
          {SHORTCUTS.map((shortcut) => {
            const Icon = shortcut.icon;
            return (
              <button
                key={shortcut.id}
                onClick={() => openTab(shortcut.tabType)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors"
                style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface)';
                }}
              >
                <Icon className="w-3.5 h-3.5 text-engramiq-green" />
                {shortcut.label}
              </button>
            );
          })}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[720px] mx-auto px-6 py-6 space-y-5">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
              >
                {msg.role === 'assistant' && (
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: 'rgba(23, 196, 128, 0.1)' }}
                  >
                    <Bot className="w-4 h-4 text-engramiq-green" />
                  </div>
                )}

                {msg.role === 'user' ? (
                  <div
                    className="max-w-[70%] px-4 py-3 rounded-2xl text-sm leading-relaxed rounded-br-sm"
                    style={{ backgroundColor: '#17c480', color: '#fff' }}
                  >
                    {msg.content}
                  </div>
                ) : (
                  <div className="max-w-[80%] space-y-1">
                    {renderParts(msg, messages, isStreaming)}
                  </div>
                )}

                {msg.role === 'user' && (
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                  >
                    <User className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Chat input */}
        <div className="eg-toolbar-header flex-shrink-0 px-6 py-4">
          <div className="max-w-[720px] mx-auto flex items-end gap-3">
            <div
              className="flex-1 relative rounded-2xl border overflow-hidden transition-shadow focus-within:shadow-md"
              style={{
                backgroundColor: 'var(--color-bg-elevated)',
                borderColor: 'var(--color-input-border)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
            >
              <textarea
                ref={chatInputRef}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Continue the conversation..."
                rows={1}
                className="w-full px-4 py-3 text-sm resize-none bg-transparent eg-text-primary outline-none max-h-32"
                style={{ caretColor: '#17c480' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 128) + 'px';
                }}
              />
            </div>
            {isStreaming ? (
              <button
                onClick={stopStreaming}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                title="Stop generating"
              >
                <Square className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!chatInput.trim()}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all"
                style={{
                  backgroundColor: chatInput.trim() ? '#17c480' : 'var(--color-bg-tertiary)',
                  color: chatInput.trim() ? '#fff' : 'var(--color-text-muted)',
                  cursor: chatInput.trim() ? 'pointer' : 'default',
                }}
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Landing mode ─────────────────────────────────────────────────

  return (
    <div
      className="h-full flex flex-col items-center overflow-y-auto"
      style={{ background: 'var(--color-zone-recessed)' }}
    >
      {/* Top spacer */}
      <div className="flex-shrink-0 h-[12vh]" />

      {/* Logo + Greeting */}
      <div className="text-center mb-8 px-4">
        <div className="mb-3">
          <Logo />
        </div>
        <p className="text-base eg-text-secondary">
          {greeting}{siteName ? `. Working on ${siteName}.` : '.'}
        </p>
      </div>

      {/* Chat input (elevated) */}
      <div className="w-full max-w-[640px] px-4 mb-8">
        <div
          className="relative rounded-2xl border overflow-hidden transition-shadow focus-within:shadow-lg"
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            borderColor: 'var(--color-input-border)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <textarea
            ref={inputRef}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about your portfolio..."
            rows={2}
            className="w-full px-5 pt-4 pb-12 text-sm resize-none bg-transparent eg-text-primary outline-none"
            style={{ caretColor: '#17c480' }}
          />
          <div className="absolute bottom-3 right-3 flex items-center gap-2">
            <span className="text-[10px] eg-text-muted">
              Enter to send
            </span>
            <button
              onClick={handleSend}
              disabled={!chatInput.trim()}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
              style={{
                backgroundColor: chatInput.trim() ? '#17c480' : 'var(--color-bg-tertiary)',
                color: chatInput.trim() ? '#fff' : 'var(--color-text-muted)',
                cursor: chatInput.trim() ? 'pointer' : 'default',
              }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Quick shortcuts */}
      <div className="w-full max-w-[700px] px-4 mb-8">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {SHORTCUTS.map((shortcut) => {
            const Icon = shortcut.icon;
            return (
              <button
                key={shortcut.id}
                onClick={() => openTab(shortcut.tabType)}
                className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all group"
                style={{ backgroundColor: 'var(--color-surface)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface)';
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'rgba(23, 196, 128, 0.1)' }}
                >
                  <Icon className="w-5 h-5 text-engramiq-green" />
                </div>
                <span className="text-[11px] font-medium eg-text-secondary text-center leading-tight">
                  {shortcut.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Inbox preview */}
      {(recentEvents.length > 0 || recentDocs.length > 0) && (
        <div className="w-full max-w-[700px] px-4 pb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider eg-text-muted">
              Recent Activity
            </h2>
            <button
              onClick={() => openTab('projectinbox')}
              className="flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80"
              style={{ color: '#17c480' }}
            >
              View Inbox <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          <div className="space-y-2">
            {recentEvents.slice(0, 5).map((event) => (
              <button
                key={event.id}
                onClick={() => openTab('eventstasks')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors eg-card-elevated"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '';
                }}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: SEVERITY_HEX_COLORS[event.severity] || '#9CA3AF' }}
                />
                <Activity className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm eg-text-primary truncate block">{event.title}</span>
                  <span className="text-[11px] eg-text-muted">
                    {event.event_type} &middot; {event.severity}
                  </span>
                </div>
                <span className="text-[11px] eg-text-muted flex-shrink-0">
                  {timeAgo(event.occurred_at)}
                </span>
              </button>
            ))}

            {recentDocs.map((doc) => (
              <button
                key={doc.id}
                onClick={() => openTab('dataroom')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors eg-card-elevated"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '';
                }}
              >
                <FileText className="w-4 h-4 flex-shrink-0 text-engramiq-green" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm eg-text-primary truncate block">
                    {doc.title || doc.filename}
                  </span>
                  <span className="text-[11px] eg-text-muted">
                    {doc.doc_type || 'Document'}
                  </span>
                </div>
                <span className="text-[11px] eg-text-muted flex-shrink-0">
                  {timeAgo(doc.created_at)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Message rendering helpers ──────────────────────────────────────

function renderParts(msg: AgentMessage, allMessages: AgentMessage[], isStreaming: boolean) {
  if (!msg.parts || msg.parts.length === 0) {
    if (isStreaming && msg === allMessages[allMessages.length - 1]) {
      return (
        <div
          className="px-4 py-3 rounded-2xl rounded-bl-sm"
          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
        >
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
        </div>
      );
    }
    return null;
  }

  return msg.parts.map((part, i) => renderPart(part, i, msg, allMessages, isStreaming));
}

function renderPart(
  part: AgentMessagePart,
  index: number,
  msg: AgentMessage,
  allMessages: AgentMessage[],
  isStreaming: boolean,
) {
  const isLastMsg = msg === allMessages[allMessages.length - 1];

  switch (part.type) {
    case 'thinking':
      return (
        <ThinkingBlock
          key={index}
          text={part.text}
          startedAt={part.startedAt}
          completedAt={part.completedAt}
          isStreaming={isStreaming && isLastMsg}
        />
      );
    case 'tool_call':
      return (
        <ToolCallCard
          key={part.eventId}
          toolName={part.toolName}
          args={part.args}
          status={part.status}
          result={part.result}
        />
      );
    case 'text':
      return (
        <StreamingMarkdown
          key={index}
          text={part.text}
          isStreaming={isStreaming && isLastMsg && index === (msg.parts?.length || 0) - 1}
        />
      );
    default:
      return null;
  }
}

export default HomeLandingTab;
